import { useEffectOnce } from 'react-use';
import styled from '@emotion/styled';

import { Button, Link, Modal, Text } from '@hotjar/hotsauce';
import {
  closedModal,
  extractAccountGroupingProps,
  loadedModal,
  trackClick,
} from '@hotjar/tracking';
import type { Site } from '@hotjar/site';
import { Trans, useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';

export interface LockedSubscriptionModalProps {
  site: Site;
}

const S = {
  ModalHeader: styled(Modal.Header)`
    h1 {
      white-space: normal;
    }
  `,
};

const MODAL_NAME = 'Locked subscription modal';
const SUBSCRIPTION_SUPPORT_LINK = '/requests/new?ticket_form_id=360000390393';

export const LockedSubscriptionModal = ({
  site,
  onCancel,
}: LockedSubscriptionModalProps & GlobalModalProps<void>): JSX.Element => {
  const { t } = useTranslationWithPrefix('upsell:locked-subscription-modal');
  const helpHotjarUrl = useHelpHotjarUrl();

  const onContactSupport = () => {
    trackClick({
      name: 'Contact Support button',
      uiPosition: MODAL_NAME,
      groupData: extractAccountGroupingProps(site.account),
    });
    window.open(`${helpHotjarUrl}${SUBSCRIPTION_SUPPORT_LINK}`, '_blank', 'noopener noreferrer');
  };

  useEffectOnce(() => {
    loadedModal(MODAL_NAME, extractAccountGroupingProps(site.account));

    return () => {
      closedModal(MODAL_NAME, extractAccountGroupingProps(site.account));
    };
  });

  return (
    <Modal appElementSelector="#root" isOpen contentLabel={t('title')} onClose={onCancel} size="s">
      <S.ModalHeader title={t('title')} />
      <Modal.Body>
        <Text as="p">{t('description')}</Text>
        <Text as="p">
          <Trans i18nKey="upsell:locked-subscription-modal.learn-more">
            For more information about Hotjar plans, see our{' '}
            <Link kind="inline" href="https://www.hotjar.com/pricing" isExternal>
              Pricing page
            </Link>
          </Trans>
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={() => onCancel()}>
          {t('cancel')}
        </Button>
        <Button kind="primary" onClick={onContactSupport}>
          {t('contact-support')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
